import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { get, merge, uniqBy } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import Row from 'reactstrap/lib/Row';
import PropTypes from 'prop-types';

import { Link } from 'site-modules/shared/components/link/link';
import { removeItem } from 'site-modules/shared/utils/profile/dashboard-helper';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { EngagementHandler } from 'client/engagement-handlers';
import { VisitorEntities } from 'client/data/models/visitor';
import { ListEngagementHandler } from 'client/engagement-handlers/list-engagement-handler/list-engagement-handler';
import { VehicleCards } from './vehicle-cards';

import './vin-cards-list.scss';

EngagementHandler.register(ListEngagementHandler);

export function VinCardsList({
  inventory = null,
  inventoryCount = 0,
  isMobile = false,
  visitorLocation = null,
  vinsWithLeads,
  visibleSeeMoreButton,
  seeMoreClick,
  savedDeals = [],
  compareSelect = null,
  updatePriceDropAlerts,
  deleteVinFromIdm,
}) {
  const removedVins = useRef([]);

  const [renderedInventory, setRenderedInventory] = useState([]);

  const onRemoveCard = useCallback(
    vin => {
      const removedVin = {
        vehicles: {
          vins: {
            [vin]: {
              franchiseId: '',
              lastViewedTs: new Date().toISOString(),
              niceMake: '',
              niceModel: '',
              year: 0,
            },
          },
        },
      };

      const savedDeal = savedDeals.find(deal => vin === deal.vin);
      deleteVinFromIdm(merge(removedVin, savedDeal && { edmundsDeals: [savedDeal] }));

      removedVins.current = [...removedVins.current, vin];
      setRenderedInventory(removeItem(renderedInventory, vin));
    },
    [deleteVinFromIdm, renderedInventory, savedDeals]
  );

  useEffect(() => {
    if (!get(inventory, 'length')) {
      return;
    }

    const nextInventory = uniqBy([...renderedInventory, ...inventory], 'vin').filter(
      inv => !removedVins.current.includes(inv.vin)
    );
    EventToolbox.fireCustomEvent(TrackingConstant.ACTION_LIST_VIEWED, {
      nextInventory,
      creativeId: 'edm-entry-dashboard',
    });

    setRenderedInventory(nextInventory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory]);

  useEffect(() => {
    updatePriceDropAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/refs -- removed VIN bookkeeping is intentionally read for loading placeholders
  const loadingVins = new Array(Math.max(0, inventoryCount - renderedInventory.length - removedVins.current.length))
    .fill()
    .map((_, index) => ({ vin: index }));

  const hasInventory = !!renderedInventory.length;
  const hasLoadingVins = !!loadingVins.length;

  return (
    <Fragment>
      {(hasInventory || hasLoadingVins) && (
        <Row tag="ul" className="dashboard-vin-cards-list list-unstyled mb-0" aria-label="Saved inventories">
          {hasInventory && (
            <VehicleCards
              isMobile={isMobile}
              visitorLocation={visitorLocation}
              vehicleCardsData={renderedInventory}
              removeCard={onRemoveCard}
              vinsWithLeads={vinsWithLeads}
              loadingInProgress={false}
              compareSelect={compareSelect}
            />
          )}
          {hasLoadingVins && <VehicleCards vehicleCardsData={loadingVins} loadingInProgress />}
        </Row>
      )}
      {hasInventory && !hasLoadingVins && visibleSeeMoreButton && (
        <div className="text-center">
          <Button color="link" className="see-more-inventories-btn text-primary-darker p-0" onClick={seeMoreClick}>
            <span className="fw-normal size-16">
              See more <span className="icon-arrow-down3 size-10" aria-hidden />
            </span>
          </Button>
        </div>
      )}
      <div className="mt-1">
        <Link to="/inventory/srp.html" data-tracking-id="add_vehicle" className="text-primary-darker">
          Find more cars like these <span className="icon-arrow-right3 size-10" aria-hidden />
        </Link>
      </div>
    </Fragment>
  );
}

VinCardsList.propTypes = {
  inventory: PropTypes.arrayOf(PropTypes.shape({})),
  inventoryCount: PropTypes.number,
  vinsWithLeads: PropTypes.shape({}).isRequired,
  deleteVinFromIdm: PropTypes.func.isRequired,
  updatePriceDropAlerts: PropTypes.func.isRequired,
  seeMoreClick: PropTypes.func.isRequired,
  visibleSeeMoreButton: PropTypes.bool.isRequired,
  savedDeals: PropTypes.arrayOf(PropTypes.shape({})),
  compareSelect: PropTypes.shape({}),
  visitorLocation: VisitorEntities.Location,
  isMobile: PropTypes.bool,
};
